import React, { FunctionComponent } from 'react';
import classnames from 'classnames';
import type { AreaPrefecture } from './constants';
import {
  EMPLOYMENT_TYPE_LINKS,
  OCCUPATION_LINKS,
  INDUSTRY_LINKS,
  AREA_SECTIONS,
  LIST_LINKS,
} from './constants';
import { useAccordionGroup } from '../../../hooks/useAccordionGroup';
import { useDevice } from '../../../hooks/device';

type FooterListedProps = {
  showSearchSection: boolean;
  channelTalkId: string | null;
  logoUrl: string;
  rootUrl: string;
};

const renderPrefecture = (pref: AreaPrefecture) => {
  if (!pref.cities || pref.cities.length === 0) {
    return <li key={pref.path}><a href={pref.path}>{pref.name}</a></li>;
  }

  const cityLinks = pref.cities.map((city, idx) => (
    <React.Fragment key={city.path}>
      {idx > 0 && '、'}
      <a href={city.path} className="mrnone">{city.name}</a>
    </React.Fragment>
  ));

  return (
    <li key={pref.path}>
      <a href={pref.path} className="mrnone">{pref.name}</a>
      {'（'}
      {cityLinks}
      {'）'}
    </li>
  );
};

export const Presentation: FunctionComponent<FooterListedProps> = ({
  showSearchSection,
  channelTalkId,
  logoUrl,
  rootUrl,
}) => {
  const { isPc } = useDevice();
  const mainAccordion = useAccordionGroup();
  const subAccordion = useAccordionGroup();

  const handleMainClick = (index: number) => {
    if (isPc) return;
    mainAccordion.toggle(index);
  };

  const handleSubClick = (index: number) => {
    if (isPc) return;
    subAccordion.toggle(index);
  };

  return (
    <footer className="l-footer">
      <div className="l-footer-width">
        {showSearchSection && (
          <section>
            <div className="l-footer-accordion">
              <div className="l-footer-accordion-c">
                <h3>求人を探す</h3>

                <div className="l-footer-accordion-c-item">
                  <h4
                    className={classnames('l-footer-accordion-c-item-title', { 'open': mainAccordion.openIndex === 0 })}
                    onClick={() => handleMainClick(0)}
                  >
                    雇用形態
                  </h4>
                  <div className="l-footer-accordion-c-item-content" ref={mainAccordion.setContentRef(0)}>
                    <ul>
                      {EMPLOYMENT_TYPE_LINKS.map(([path, label]) => (
                        <li key={path}><a href={path}>{label}</a></li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="l-footer-accordion-c-item">
                  <h4
                    className={classnames('l-footer-accordion-c-item-title', { 'open': mainAccordion.openIndex === 1 })}
                    onClick={() => handleMainClick(1)}
                  >
                    職種
                  </h4>
                  <div className="l-footer-accordion-c-item-content" ref={mainAccordion.setContentRef(1)}>
                    <ul>
                      {OCCUPATION_LINKS.map(([path, label]) => (
                        <li key={path}><a href={path}>{label}</a></li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="l-footer-accordion-c-item">
                  <h4
                    className={classnames('l-footer-accordion-c-item-title', { 'open': mainAccordion.openIndex === 2 })}
                    onClick={() => handleMainClick(2)}
                  >
                    業種
                  </h4>
                  <div className="l-footer-accordion-c-item-content" ref={mainAccordion.setContentRef(2)}>
                    <ul>
                      {INDUSTRY_LINKS.map(([path, label]) => (
                        <li key={path}><a href={path}>{label}</a></li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="l-footer-accordion-c-item">
                  <h4
                    className={classnames('l-footer-accordion-c-item-title', { 'open': mainAccordion.openIndex === 3 })}
                    onClick={() => handleMainClick(3)}
                  >
                    エリア
                  </h4>
                  <div className="l-footer-accordion-c-item-content" ref={mainAccordion.setContentRef(3)}>
                    <div className="ul-sub">
                      {AREA_SECTIONS.map((area, areaIndex) => (
                        <div key={area.areaPath}>
                          <div
                            className={classnames('ul-sub-title', { 'open-sub': subAccordion.openIndex === areaIndex })}
                            onClick={() => handleSubClick(areaIndex)}
                          >
                            <a href={area.areaPath}>{area.areaName}</a>
                          </div>
                          <div className="ul-sub-content" ref={subAccordion.setContentRef(areaIndex)}>
                            <ul>
                              {area.prefectures.map((pref) => renderPrefecture(pref))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="l-footer-accordion-c-item m-n">
                  <h4
                    className={classnames('l-footer-accordion-c-item-title', { 'open': mainAccordion.openIndex === 4 })}
                    onClick={() => handleMainClick(4)}
                  >
                    一覧
                  </h4>
                  <div className="l-footer-accordion-c-item-content" ref={mainAccordion.setContentRef(4)}>
                    <ul>
                      {LIST_LINKS.map(([path, label]) => (
                        <li key={path}><a href={path}>{label}</a></li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className={classnames({ 'is-top-padding': !showSearchSection })}>
          <div className="l-footer-accordion l-footer-accordion-sec">
            <div className="l-footer-accordion-c l-footer-accordion-sec-c">
              <h3>READY TO FASHIONについて</h3>
              <ul>
                <li><a href="/company">運営会社</a></li>
                <li><a href={`${rootUrl}mag/first-guide/`} target="_blank" rel="noreferrer">初めてガイド[ユーザー]</a></li>
                <li><a href={`${rootUrl}mag/guide/`} target="_blank" rel="noreferrer">よくある質問[ユーザー]</a></li>
                <li><a href="https://jinji.readytofashion.jp/faq" target="_blank" rel="noreferrer">よくある質問[企業]</a></li>
                <li><a href="https://jinji.readytofashion.jp/service" target="_blank" rel="noreferrer">採用担当者の方へ</a></li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <div className="l-footer-accordion l-footer-accordion-sec">
            <div className="l-footer-accordion-c l-footer-accordion-sec-c">
              <h3>提供サービスについて</h3>
              <ul>
                <li><a href={`${rootUrl}mag/`} target="_blank" rel="noreferrer">READY TO FASHION MAG</a></li>
                <li><a href="https://jinji.readytofashion.jp" target="_blank" rel="noreferrer">READY TO FASHION JINJI</a></li>
                <li><a href={`${rootUrl}mag/fashioncommunity1_0.html`} target="_blank" rel="noreferrer">fashion community 1.0</a></li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <div className="l-footer-accordion l-footer-accordion-sec">
            <div className="l-footer-accordion-c l-footer-accordion-sec-c">
              <h3>その他</h3>
              <ul>
                <li><a href="/privacy" target="_blank" rel="noreferrer">プライバシーポリシー</a></li>
                <li><a href="/users/terms" target="_blank" rel="noreferrer">利用規約[ユーザー]</a></li>
                <li><a href="/companies/terms" target="_blank" rel="noreferrer">利用規約[企業]</a></li>
                <li><a href="/contact" target="_blank" rel="noreferrer">お問い合わせ</a></li>
              </ul>
            </div>
          </div>
        </section>

        <div className="l-t">
          <div className="l-t-logo">
            <a href="/">
              <img src={logoUrl} alt="READY TO FASHIONの会社ロゴ" />
            </a>
          </div>
          <small className="l-t-copyright" id={channelTalkId || undefined}>
            Copyright READY TO FASHION. Inc, All Rights Reserved.
          </small>
        </div>
      </div>
    </footer>
  );
};
